/*
 * The third-party marks the mockups show (Google "G", Google Docs page,
 * Google Maps pin, ChatGPT knot, video play button, WhatsApp bubble), drawn
 * as inline SVG for this local demo, no logo files. Always decorative: every
 * mark is aria-hidden and sits next to visible text that carries the meaning.
 */

const svgProps = (size, className) => ({
  width: size,
  height: size,
  viewBox: '0 0 48 48',
  className,
  'aria-hidden': true,
  focusable: 'false',
});

/* The Google "G": the four-colour mark, drawn to its published shape. */
export function GoogleMark({ size = 28, className }) {
  return (
    <svg {...svgProps(size, className)}>
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5Z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65Z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19Z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48Z"
      />
    </svg>
  );
}

/* A Google Docs-style document: blue page, folded corner, white text lines. */
export function DocsMark({ size = 28, className }) {
  return (
    <svg {...svgProps(size, className)}>
      <path d="M12.5 3h16L39 13.5V42a3 3 0 0 1-3 3H12.5a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3Z" fill="#4285F4" />
      <path d="M28.5 13.5 39 24V13.5Z" fill="#3367D6" opacity="0.55" />
      <path d="M28.5 3v8a2.5 2.5 0 0 0 2.5 2.5h8Z" fill="#A1C2FA" />
      <g fill="#fff">
        <rect x="16" y="22" width="16" height="2.4" rx="0.6" />
        <rect x="16" y="27" width="16" height="2.4" rx="0.6" />
        <rect x="16" y="32" width="16" height="2.4" rx="0.6" />
        <rect x="16" y="37" width="11" height="2.4" rx="0.6" />
      </g>
    </svg>
  );
}

/*
 * The ChatGPT knot: six interlinked rounded links, drawn to the published
 * mark. `color` sets the knot (white on a teal tile, teal or black alone).
 */
export function AssistantMark({ size = 28, className, color = '#10a37f' }) {
  return (
    <svg {...svgProps(size, className)} viewBox="0 0 24 24">
      <path
        fill={color}
        d="M22.28 9.82a5.98 5.98 0 0 0-.52-4.91 6.05 6.05 0 0 0-6.51-2.9A6.07 6.07 0 0 0 4.98 4.18a5.98 5.98 0 0 0-4 2.9 6.05 6.05 0 0 0 .74 7.1 5.98 5.98 0 0 0 .51 4.91 6.05 6.05 0 0 0 6.52 2.9A5.98 5.98 0 0 0 13.26 24a6.06 6.06 0 0 0 5.77-4.21 5.99 5.99 0 0 0 4-2.9 6.06 6.06 0 0 0-.75-7.07Zm-9.02 12.61a4.48 4.48 0 0 1-2.88-1.04l.14-.08 4.78-2.76a.79.79 0 0 0 .39-.68v-6.74l2.02 1.17a.07.07 0 0 1 .04.05v5.58a4.5 4.5 0 0 1-4.49 4.5Zm-9.66-4.13a4.47 4.47 0 0 1-.53-3.01l.14.09 4.78 2.76a.77.77 0 0 0 .78 0l5.84-3.37v2.33a.08.08 0 0 1-.03.06l-4.84 2.79a4.5 4.5 0 0 1-6.14-1.65ZM2.34 7.9a4.49 4.49 0 0 1 2.37-1.97V11.6a.77.77 0 0 0 .39.68l5.81 3.35-2.02 1.17a.08.08 0 0 1-.07 0l-4.83-2.79A4.5 4.5 0 0 1 2.34 7.87Zm16.6 3.86-5.84-3.39 2.02-1.16a.08.08 0 0 1 .07 0l4.83 2.79a4.49 4.49 0 0 1-.68 8.1v-5.68a.79.79 0 0 0-.4-.67Zm2.01-3.02-.14-.09-4.77-2.78a.78.78 0 0 0-.79 0L9.41 9.23V6.9a.07.07 0 0 1 .03-.06l4.83-2.79a4.5 4.5 0 0 1 6.68 4.66ZM8.31 12.86l-2.02-1.16a.08.08 0 0 1-.04-.06V6.07a4.5 4.5 0 0 1 7.38-3.45l-.14.08L8.7 5.46a.79.79 0 0 0-.39.68Zm1.1-2.37 2.6-1.5 2.61 1.5v3l-2.6 1.5-2.61-1.5Z"
      />
    </svg>
  );
}

/* A red rounded rectangle with a white play triangle. */
export function VideoMark({ size = 28, className }) {
  return (
    <svg {...svgProps(size, className)}>
      <rect x="4" y="11" width="40" height="27" rx="8" fill="#ff1f1f" />
      <path d="M20 18.5v12l10.5-6Z" fill="#fff" />
    </svg>
  );
}

/*
 * The Google Maps pin: blue, red, light-blue, yellow and green segments
 * around a see-through centre, drawn to the published icon.
 */
export function MapsMark({ size = 28, className }) {
  return (
    <svg {...svgProps(size, className)} viewBox="-20 0 132.3 132.3">
      <path fill="#1a73e8" d="M60.2 2.2C55.8.8 51 0 46.1 0 32 0 19.3 6.4 10.8 16.5l21.8 18.3L60.2 2.2Z" />
      <path fill="#ea4335" d="M10.8 16.5C4.1 24.5 0 34.9 0 46.1c0 8.7 1.7 15.7 4.6 22l28-33.3-21.8-18.3Z" />
      <path
        fill="#4285f4"
        d="M46.2 28.5c9.8 0 17.7 7.9 17.7 17.7 0 4.3-1.6 8.3-4.2 11.4 0 0 13.9-16.6 27.5-32.7-5.6-10.8-15.3-19-27-22.7L32.6 34.8c3.3-3.8 8.1-6.3 13.6-6.3"
      />
      <path
        fill="#fbbc04"
        d="M46.2 63.8c-9.8 0-17.7-7.9-17.7-17.7 0-4.3 1.5-8.3 4.1-11.3l-28 33.3c4.8 10.6 12.8 19.2 21 29.9l34.1-40.5c-3.3 3.9-8.1 6.3-13.5 6.3"
      />
      <path
        fill="#34a853"
        d="M59.1 109.2c15.4-24.1 33.3-35 33.3-63 0-7.7-1.9-14.9-5.2-21.3L25.6 98c2.6 3.4 5.3 7.3 7.9 11.3 9.4 14.5 6.8 23.1 12.8 23.1s3.4-8.7 12.8-23.2"
      />
    </svg>
  );
}

/* A green speech bubble with a white handset. */
export function WhatsAppMark({ size = 28, className }) {
  return (
    <svg {...svgProps(size, className)}>
      <path d="M24 4a20 20 0 0 0-17.3 30L4 44l10.3-2.7A20 20 0 1 0 24 4Z" fill="#25d366" />
      <path
        d="M24 8a16 16 0 0 0-13.6 24.4l.4.6-1.6 5.8 6-1.6.6.3A16 16 0 1 0 24 8Z"
        fill="none"
        stroke="#fff"
        strokeWidth="2.4"
      />
      <path
        d="M18.6 15.6c.5-.1 1 .1 1.2.6l1.6 3.6c.2.5.1 1-.3 1.4l-1.2 1.3c1 2.2 2.8 4 5 5l1.3-1.2c.4-.4 1-.5 1.4-.3l3.6 1.6c.5.2.8.7.6 1.2l-.5 2.2c-.2.8-.9 1.3-1.7 1.3-7.3-.3-13.1-6.1-13.4-13.4 0-.8.5-1.5 1.3-1.7Z"
        fill="#fff"
      />
    </svg>
  );
}
